import React, { useEffect, useState, useCallback } from "react";
import { Button, Spin, Tooltip, Tag, Collapse, Badge } from "antd";
import {
  SettingOutlined,
  InfoCircleOutlined,
  ArrowRightOutlined,
  KeyOutlined,
  SwapOutlined,
  FolderOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import NewReconciliationWizard from "../../components/compare/NewReconciliationWizard";
import DashboardWidget from "../../components/dashboard/DashboardWidget";
import RunStatusBadge from "../../components/dashboard/RunStatusBadge";
import { getAllConnection } from "../../service/db.service";
import {
  getMappingNames,
  getMappingByName,
} from "../../service/mapping.service";
import { getAllResultSummary } from "../../service/result.service";
import { labelForSubCategory } from "../../constants/reconciliationLabels";
import "../../styles/db.css";
import "../../styles/compare.css";
import { THEME } from "../../styles/colors";

// Parse "dd-MM-yyyy HH:mm:ss" → ms timestamp (0 if unparseable)
const parseDateStr = (str) => {
  if (!str) return 0;
  try {
    const [datePart, timePart] = str.split(" ");
    const [dd, mm, yyyy] = datePart.split("-");
    return new Date(`${yyyy}-${mm}-${dd}T${timePart}`).getTime();
  } catch {
    return 0;
  }
};

// ── Compare type definitions ──────────────────────────────────
const COMPARE_TYPES = [
  {
    key: "filetofile",
    label: "File to File",
    shortLabel: "F → F",
    description: "Reconcile two files against each other using a spec definition",
    icon: (
      <svg
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="16" y2="17" />
      </svg>
    ),
    tooltip: "Reconcile two flat files using a control document spec",
    color: THEME.primary,
    bg: "rgba(11, 31, 58, 0.08)",
    tagColor: "#E8EEF6",
    tagText: "#0B1F3A",
  },
  {
    key: "dbtofile",
    label: "DB to File",
    shortLabel: "DB → F",
    description: "Run a database query and reconcile the output against a file",
    icon: (
      <svg
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        <line x1="17" y1="14" x2="21" y2="18" />
      </svg>
    ),
    tooltip: "Extract data from a database and reconcile against a flat file",
    color: THEME.secondary,
    bg: "rgba(17, 43, 76, 0.12)",
    tagColor: "#E6EDF5",
    tagText: "#185FA5",
  },
  {
    key: "dbtodb",
    label: "DB to DB",
    shortLabel: "DB → DB",
    description: "Reconcile datasets across two database connections",
    icon: (
      <svg
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <ellipse cx="7" cy="6" rx="5" ry="2.5" />
        <path d="M2 6v5c0 1.38 2.24 2.5 5 2.5s5-1.12 5-2.5V6" />
        <ellipse cx="17" cy="12" rx="5" ry="2.5" />
        <path d="M12 12v5c0 1.38 2.24 2.5 5 2.5s5-1.12 5-2.5v-5" />
        <path d="M2 11v5c0 1.38 2.24 2.5 5 2.5" />
        <line x1="10" y1="8.5" x2="12" y2="10" />
      </svg>
    ),
    tooltip: "Reconcile datasets across two different database environments",
    color: THEME.tableMuted,
    bg: "rgba(108, 138, 155, 0.22)",
    tagColor: "#EDEEF0",
    tagText: "#4B5563",
  },
];

// ── Helpers ───────────────────────────────────────────────────
const TYPE_LABELS = {
  "File to File": { label: "F → F", color: "#E8EEF6", text: "#0B1F3A" },
  "File to Database": { label: "DB → F", color: "#E6EDF5", text: "#185FA5" },
  "File to Specification": {
    label: "F → Spec",
    color: "#E8EEF6",
    text: "#0B1F3A",
  },
  Database: { label: "DB → DB", color: "#EDEEF0", text: "#4B5563" },
  "Database to Database": {
    label: "DB → DB",
    color: "#EDEEF0",
    text: "#4B5563",
  },
};

function TypeTag({ subCategory }) {
  const cfg = TYPE_LABELS[subCategory] || {
    label: labelForSubCategory(subCategory) || "Reconciliation",
    color: "#EDEEF0",
    text: "#4B5563",
  };
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: 10,
        fontWeight: 600,
        padding: "1px 7px",
        borderRadius: 10,
        background: cfg.color,
        color: cfg.text,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
}

// ── Recent Compare Runs widget ────────────────────────────────
function RecentRuns({ onViewAll }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAllResultSummary()
      .then((r) => {
        const filtered = (r.data || [])
          .filter((row) => row.category === "Compare")
          .sort((a, b) => parseDateStr(b.runStart) - parseDateStr(a.runStart))
          .slice(0, 20);
        setRuns(filtered);
      })
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardWidget
      title="Recent Reconciliation Runs"
      accent={THEME.primary}
      loading={loading}
      actions={
        <Button
          type="link"
          size="small"
          style={{ fontSize: 12, padding: 0 }}
          icon={<ArrowRightOutlined />}
          onClick={onViewAll}
        >
          View all
        </Button>
      }
      noPad
      style={{ height: "100%" }}
      bodyStyle={{ flex: 1, overflowY: "auto", padding: 0, minHeight: 0 }}
    >
      {runs.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "36px 16px",
            gap: 8,
          }}
        >
          <SwapOutlined
            style={{ fontSize: 28, opacity: 0.2, color: "#6B7280" }}
          />
          <div style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>
            No reconciliation runs yet
          </div>
          <div style={{ fontSize: 12, color: "#9CA3AF" }}>
            Run your first reconciliation using the cards above
          </div>
        </div>
      ) : (
        <div style={{ overflowY: "auto", height: "100%" }}>
          {/* Header row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "90px 70px 1fr 140px",
              gap: 8,
              padding: "7px 16px",
              borderBottom: "1px solid #E5E7EB",
              background: "#F3F4F6",
            }}
          >
            {["Status", "Type", "Suite / Mapping", "Run date"].map((h) => (
              <div
                key={h}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: ".5px",
                }}
              >
                {h}
              </div>
            ))}
          </div>

          {/* Data rows */}
          {runs.map((row, i) => (
            <div
              key={row.resultSummaryKey || i}
              style={{
                display: "grid",
                gridTemplateColumns: "90px 70px 1fr 140px",
                gap: 8,
                padding: "8px 16px",
                alignItems: "center",
                borderBottom: "1px solid #E5E7EB",
                background: i % 2 === 0 ? "#ffffff" : "#F9FAFB",
              }}
            >
              <div>
                <RunStatusBadge status={row.testResult} size="sm" />
              </div>
              <div>
                <TypeTag subCategory={row.subCategory} />
              </div>
              <div
                style={{
                  fontSize: 12,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#111827",
                }}
              >
                {row.specName || row.suiteName || row.subCategory || "—"}
              </div>
              <div style={{ fontSize: 11, color: "#6B7280" }}>
                {row.runStart ? row.runStart.substring(0, 16) : "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardWidget>
  );
}

// ── Mapping Library widget ────────────────────────────────────
function MappingLibrary({ onLoadMapping }) {
  const [names, setNames] = useState([]);
  const [detailCache, setDetailCache] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMappingNames()
      .then((r) => setNames(r.data || []))
      .catch(() => setNames([]))
      .finally(() => setLoading(false));
  }, []);

  const loadDetail = useCallback(
    (name) => {
      if (detailCache[name]) return;
      getMappingByName(name)
        .then((r) =>
          setDetailCache((prev) => ({ ...prev, [name]: r.data || [] })),
        )
        .catch(() => setDetailCache((prev) => ({ ...prev, [name]: [] })));
    },
    [detailCache],
  );

  const panels = names.map((name) => ({
    key: name,
    style: { borderBottom: "1px solid #E5E7EB", borderRadius: 0 },
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <FolderOutlined style={{ color: "#185FA5", fontSize: 13 }} />
        <span style={{ fontSize: 13, fontWeight: 500, color: "#1F2937" }}>
          {name}
        </span>
        {detailCache[name] && (
          <Badge
            count={detailCache[name].length}
            style={{
              background: "#DBEAFE",
              color: "#1D4ED8",
              fontSize: 10,
              boxShadow: "none",
            }}
          />
        )}
      </div>
    ),
    children: (
      <div style={{ background: "#F9FAFB", padding: "12px 16px" }}>
        <MappingDetail
          name={name}
          rows={detailCache[name]}
          onLoad={onLoadMapping}
        />
      </div>
    ),
  }));

  return (
    <DashboardWidget
      title="Mapping Library"
      accent="#185FA5"
      loading={loading}
      subtitle="Saved field mappings"
      noPad
      style={{ height: "100%" }}
      bodyStyle={{ flex: 1, overflowY: "auto", padding: 0, minHeight: 0 }}
    >
      {names.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "36px 16px",
            color: "#6B7280",
            gap: 8,
          }}
        >
          <FolderOutlined
            style={{ fontSize: 28, opacity: 0.25, color: "#6B7280" }}
          />
          <div style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>
            No saved mappings yet
          </div>
          <div style={{ fontSize: 12, color: "#9CA3AF", textAlign: "center" }}>
            Mappings are saved when you run a reconciliation
          </div>
        </div>
      ) : (
        <Collapse
          accordion
          onChange={(key) => {
            if (key) loadDetail(key);
          }}
          bordered={false}
          style={{ borderRadius: 0, background: "transparent" }}
          expandIconPosition="end"
          items={panels}
        />
      )}
    </DashboardWidget>
  );
}

function MappingDetail({ name, rows, onLoad }) {
  if (!rows) {
    return (
      <div style={{ padding: "12px 0", textAlign: "center" }}>
        <Spin size="small" />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div style={{ fontSize: 12, color: "#6B7280", padding: "8px 0" }}>
        No fields in this mapping.
      </div>
    );
  }

  const rowKeys = rows.filter((r) => r.rowKey);
  const dataRows = rows.filter((r) => !r.rowKey);
  const dateFields = rows.filter((r) => r.fieldType && r.fieldType !== "TEXT");

  return (
    <div>
      {/* Row key summary chips */}
      {rowKeys.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          <span style={{ fontSize: 11, color: "#6B7280", alignSelf: "center" }}>
            Row keys:
          </span>
          {rowKeys.map((r, i) => (
            <span
              key={i}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                fontFamily: "monospace",
                fontWeight: 600,
                padding: "1px 8px",
                borderRadius: 10,
                background: "#E8EEF6",
                color: "#0B1F3A",
              }}
            >
              <KeyOutlined style={{ fontSize: 10 }} />
              {r.source1}
            </span>
          ))}
        </div>
      )}

      {/* Field mapping table */}
      <div
        style={{
          background: "var(--color-background-primary)",
          borderRadius: 6,
          border: "0.5px solid #D1D5DB",
          overflow: "hidden",
          marginBottom: 12,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 24px 1fr",
            gap: 8,
            padding: "5px 10px",
            background: "#F3F4F6",
            borderBottom: "0.5px solid #D1D5DB",
          }}
        >
          {["Source 1", "", "Source 2"].map((h, i) => (
            <div
              key={i}
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#374151",
                textTransform: "uppercase",
                letterSpacing: ".5px",
                textAlign: i === 1 ? "center" : "left",
              }}
            >
              {h}
            </div>
          ))}
        </div>

        {/* Data rows */}
        {dataRows.map((row, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 24px 1fr",
              gap: 8,
              alignItems: "center",
              padding: "5px 10px",
              borderBottom:
                i < dataRows.length - 1 ? "0.5px solid #E5E7EB" : "none",
              background: i % 2 === 0 ? "#fff" : "#F9FAFB",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontFamily: "monospace",
                color: "#111827",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.source1}
            </div>
            <div
              style={{ fontSize: 11, color: "#9CA3AF", textAlign: "center" }}
            >
              →
            </div>
            <div
              style={{
                fontSize: 12,
                fontFamily: "monospace",
                color: "#111827",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.source2}
            </div>
          </div>
        ))}
      </div>

      {/* Date field type badges */}
      {dateFields.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          <span style={{ fontSize: 11, color: "#6B7280", alignSelf: "center" }}>
            Date fields:
          </span>
          {dateFields.map((r, i) => (
            <span
              key={i}
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "1px 7px",
                borderRadius: 10,
                background: "#DBEAFE",
                color: "#1D4ED8",
              }}
            >
              {r.source1} · {r.fieldType}
            </span>
          ))}
        </div>
      )}

      {/* Load button */}
      <Button
        size="small"
        style={{
          fontSize: 12,
          height: 28,
          background: "#0B1F3A",
          borderColor: "#0B1F3A",
          color: "#fff",
        }}
        onClick={() => onLoad(name)}
      >
        Load into reconciliation →
      </Button>
    </div>
  );
}

// ── Main Compare page ─────────────────────────────────────────
function Compare() {
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  // Warm the button's loading state while connections load; the wizard fetches
  // its own connections/environments on open.
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);

  useEffect(() => {
    setConnectionsLoaded(true);
    getAllConnection().finally(() => setConnectionsLoaded(false));
  }, []);

  // Quick-start seed for the unified wizard, keyed off the type card. File↔DB is
  // seeded file-first (A=file) so the common path stays identical to today.
  const SEED_BY_TYPE = {
    filetofile: { aKind: "file", bKind: "file" },
    dbtofile: { aKind: "file", bKind: "db" },
    dbtodb: { aKind: "db", bKind: "db" },
  };
  const [wizardSeed, setWizardSeed] = useState(null);
  const [wizardMapping, setWizardMapping] = useState(null);

  // "New Reconciliation" — open the wizard unseeded (no source type pre-chosen).
  const handleNewCompare = () => {
    setWizardSeed(null);
    setWizardMapping(null);
    setIsOpen(true);
  };
  // Type card = quick-start: same wizard, two source kinds pre-filled.
  const handleQuickStart = (typeKey) => {
    setActiveType(typeKey);
    setWizardSeed(SEED_BY_TYPE[typeKey] || null);
    setWizardMapping(null);
    setIsOpen(true);
  };
  const handleCloseModal = () => setIsOpen(false);

  // From the Mapping Library — open the wizard carrying the mapping name so it's
  // preselected as an existing mapping at the Map-fields step.
  const handleLoadMapping = (mappingName) => {
    setWizardSeed(null);
    setWizardMapping(mappingName || null);
    setIsOpen(true);
  };

  return (
    <div className="compare-page">
      {/* Page header */}
      <div className="compare-page-header">
        <div>
          <h2 className="compare-title">
            Reconciliation{" "}
            <Tooltip title="Click New Reconciliation, or pick a quick-start card to seed the two sources">
              <InfoCircleOutlined
                style={{ color: THEME.tableMuted, fontSize: 14 }}
              />
            </Tooltip>
          </h2>
          <p className="compare-subtitle">
            Reconcile two sources — files or databases, in any combination
          </p>
        </div>
        <Button
          className="db-add"
          onClick={handleNewCompare}
          disabled={connectionsLoaded}
        >
          {connectionsLoaded ? (
            <Spin
              indicator={<SettingOutlined style={{ fontSize: 14 }} spin />}
            />
          ) : (
            "New Reconciliation"
          )}
        </Button>
      </div>

      {/* Type cards = quick-starts: open the same wizard, sources pre-seeded */}
      <div className="compare-cards">
        {COMPARE_TYPES.map((type) => (
          <div
            key={type.key}
            className={`compare-card ${activeType === type.key ? "compare-card--active" : ""}`}
            onClick={() => handleQuickStart(type.key)}
            style={{
              "--card-color": type.color,
              "--card-bg": type.bg,
              borderColor: activeType === type.key ? type.color : "transparent",
            }}
          >
            <div
              className="compare-card-icon"
              style={{
                background: activeType === type.key ? type.color : type.bg,
                color: activeType === type.key ? "#fff" : type.color,
              }}
            >
              {type.icon}
            </div>
            <div className="compare-card-body">
              <div className="compare-card-label">
                {labelForSubCategory(type.label)}
              </div>
              <div className="compare-card-desc">{type.description}</div>
            </div>
            {activeType === type.key && (
              <div className="compare-card-check">✓</div>
            )}
          </div>
        ))}
      </div>


      {/* ── Two-column widget area ──────────────────────────── */}
      <div className="compare-widgets">
        <div className="compare-widget-main">
          <RecentRuns onViewAll={() => navigate("/results")} />
        </div>
        <div className="compare-widget-side">
          <MappingLibrary onLoadMapping={handleLoadMapping} />
        </div>
      </div>

      {/* Unified New Reconciliation wizard (replaces the three per-type modals) */}
      <NewReconciliationWizard
        isOpen={isOpen}
        onClose={handleCloseModal}
        seed={wizardSeed}
        initialMappingName={wizardMapping}
      />
    </div>
  );
}

export default Compare;
